import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import { SidePanelFooter } from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { Dot, LastUpdateBadge } from '@/pages/Finances';
import { TopicSuggestionResource } from '@/types/resources';
import { api } from '@/utils/api';
import { scrollBarStyles } from '@/utils/theme';

import AiSuggestionsLoader from './AiSuggestionsLoader';

const Dialog = styled(StyledDialog)`
  width: 1120px;
  padding: 32px 24px 0;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  background: linear-gradient(
    90deg,
    rgba(151, 149, 240, 0.25) 0%,
    rgba(251, 200, 212, 0.25) 100%
  );
  border-radius: 4px;
  padding: 24px;
  position: relative;
`;

const StyledCloseButton = styled(CloseButton)`
  top: 28px;
  right: 24px;
`;

const HeaderBubble = styled.div`
  border-radius: 50%;
  margin-right: 18px;
  svg {
    border-radius: 50%;
    border: solid 2px ${({ theme }) => theme.colors.white};
  }
`;

const ModalHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const TopicBlock = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  padding: 16px 16px 16px 24px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoticeBox = styled.div`
  padding: 8px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  margin-bottom: 16px;
`;

const RegenerateButton = styled(Button)`
  padding: 0 16px;
  font-weight: 600;
`;

const SuggestionRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const SuggestionTitle = styled.div<{ $dimmed?: boolean }>`
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  padding: 16px 24px 16px 42px;
  flex: 1;
  text-align: left;
  position: relative;
  margin-bottom: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    background: ${({ theme }) => theme.colors.purple};
    height: 6px;
    width: 6px;
    border-radius: 50%;
    top: 25px;
    left: 24px;
    ${({ $dimmed, theme }) =>
      $dimmed &&
      css`
        background: ${theme.colors.purpleLight};
      `}
  }
`;

const Ul = styled.ul`
  list-style: initial;
  padding-left: 20px;
`;

const Li = styled.li`
  margin-bottom: 10px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLastUpdateBadge = styled(LastUpdateBadge)`
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  height: 38px;
  margin-right: 16px;
`;

const ModalContent = styled.div`
  overflow: auto;
  max-height: calc(86vh - 340px);
  padding-bottom: 8px;
  ${scrollBarStyles};
`;

const SuggestionContent = styled.div`
  text-align: left;
  flex: 2;
  padding: 24px;
  padding-top: 16px;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  align-self: stretch;
`;

const StyledSidePanelFooter = styled(SidePanelFooter)`
  margin-left: -24px;
  margin-right: -24px;
  width: calc(100% + 48px);
  text-align: right;
`;

const TopicBlockButtons = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const TopicName = styled(Text)`
  text-transform: capitalize;
`;

interface Props {
  topic: string;
  topicId: number;
  onClose: () => void;
}

const AiSuggestionsModal: FC<Props> = ({ onClose, topic, topicId }) => {
  const [refetchQuery, setRefetchQuery] = useState(false);

  const alerts = useAlerts();

  const fetchData = async () => {
    const result = await api.get<never, TopicSuggestionResource>(
      `/v1/ai/topics-suggestions/${topicId}`,
      {
        params: {
          ...(refetchQuery && { refetch: 1 }),
        },
      },
    );
    setRefetchQuery(false);
    return result;
  };

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['ai_topic_suggestion', topicId],
    fetchData,
    {
      staleTime: 300 * 1000,
      retry: 1,
      onError: (err) => {
        if ((err as any).response.status === 422) {
          return alerts.error('Api failed to provide data');
        }
        if ((err as any).response.status === 429) {
          return alerts.error('Exceeded api calls limit for today');
        }
        return alerts.error;
      },
    },
  );

  useEffect(() => {
    refetchQuery && refetch();
  }, [refetchQuery, refetch]);

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        {isLoading || isFetching ? (
          <AiSuggestionsLoader />
        ) : (
          <>
            <ModalHeader>
              <ModalHeaderLeft>
                <HeaderBubble>
                  <svg
                    width="33"
                    height="32"
                    viewBox="0 0 33 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width="32"
                      height="32"
                      transform="translate(0.5)"
                      fill="white"
                    />
                    <g filter="url(#filter0_f_6741_181932)">
                      <path
                        d="M22.4198 8.14204C27.9085 9.42911 28.2982 3.897 32.6973 3.13806C48.3249 0.441929 49.8074 38.0624 33.7275 43.7341C18.5145 49.1 2.84579 18.2481 2.84579 18.2481C2.84579 18.2481 -0.182236 10.0743 2.84579 6.7684C5.57849 3.78492 9.14214 3.92943 13.0498 4.97776C16.137 5.80599 19.3078 7.4123 22.4198 8.14204Z"
                        fill="#4136CF"
                      />
                    </g>
                    <g filter="url(#filter1_f_6741_181932)">
                      <path
                        d="M-39.3314 -20.2563C-31.252 -18.3609 -30.6783 -26.5079 -24.2029 -27.6255C-1.1992 -31.5961 0.982948 23.8066 -22.6864 32.1592C-45.0798 40.0614 -68.144 -5.37335 -68.144 -5.37335C-68.144 -5.37335 -72.6012 -17.4107 -68.144 -22.2792C-64.1215 -26.6729 -58.8758 -26.4601 -53.1239 -24.9163C-48.5795 -23.6966 -43.9122 -21.331 -39.3314 -20.2563Z"
                        fill="#00B0FF"
                      />
                    </g>
                    <g filter="url(#filter2_f_6741_181932)">
                      <ellipse
                        cx="11.0384"
                        cy="51.1661"
                        rx="27.2786"
                        ry="27.2481"
                        fill="#0038FF"
                      />
                    </g>
                    <g filter="url(#filter3_f_6741_181932)">
                      <path
                        d="M-32.3636 29.6378C-35.4202 38.7768 -36.7575 45.7411 -32.3636 54.3165C-23.797 71.0355 6.88269 70.9035 15.7016 54.3165C22.672 41.2065 12.4961 30.6695 5.08946 17.8014C1.82129 12.1234 0.973804 7.39179 -4.75416 4.21922C-17.5718 -2.88014 -27.7129 15.7325 -32.3636 29.6378Z"
                        fill="#FF4880"
                      />
                    </g>
                    <g filter="url(#filter4_f_6741_181932)">
                      <ellipse
                        cx="-37.9887"
                        cy="54.8013"
                        rx="21.7496"
                        ry="21.719"
                        fill="#FF8000"
                      />
                    </g>
                    <defs>
                      <filter
                        id="filter0_f_6741_181932"
                        x="-29.0472"
                        y="-27.5472"
                        width="104.715"
                        height="102.455"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="15.2736"
                          result="effect1_foregroundBlur_6741_181932"
                        />
                      </filter>
                      <filter
                        id="filter1_f_6741_181932"
                        x="-100.672"
                        y="-58.376"
                        width="125.305"
                        height="122.005"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="15.2736"
                          result="effect1_foregroundBlur_6741_181932"
                        />
                      </filter>
                      <filter
                        id="filter2_f_6741_181932"
                        x="-46.7874"
                        y="-6.62914"
                        width="115.651"
                        height="115.59"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="15.2736"
                          result="effect1_foregroundBlur_6741_181932"
                        />
                      </filter>
                      <filter
                        id="filter3_f_6741_181932"
                        x="-65.7269"
                        y="-27.8899"
                        width="114.307"
                        height="125.243"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="15.2736"
                          result="effect1_foregroundBlur_6741_181932"
                        />
                      </filter>
                      <filter
                        id="filter4_f_6741_181932"
                        x="-90.2855"
                        y="2.53504"
                        width="104.594"
                        height="104.532"
                        filterUnits="userSpaceOnUse"
                        colorInterpolationFilters="sRGB"
                      >
                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="BackgroundImageFix"
                          result="shape"
                        />
                        <feGaussianBlur
                          stdDeviation="15.2736"
                          result="effect1_foregroundBlur_6741_181932"
                        />
                      </filter>
                    </defs>
                  </svg>
                </HeaderBubble>
                <Text $type="button">
                  Generated AI Ideas and Summaries to relevant topic
                </Text>
              </ModalHeaderLeft>
              <StyledCloseButton onClick={onClose}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13 13L1 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </StyledCloseButton>
            </ModalHeader>
            <TopicBlock>
              <Text $type="button">
                Topic:{' '}
                <Text $type="button" as="span" $color="gray">
                  {topic}
                </Text>
              </Text>
              <TopicBlockButtons>
                {!!data?.updatedAt && (
                  <StyledLastUpdateBadge>
                    <Dot />
                    <Text as="span" $type="smallBold">
                      Last generate:{' '}
                      {DateTime.fromISO(data.updatedAt).toFormat('dd/LL/yyyy')}
                    </Text>
                  </StyledLastUpdateBadge>
                )}
                <RegenerateButton
                  disabled={refetchQuery}
                  color="colorized"
                  onClick={() => {
                    setRefetchQuery(true);
                  }}
                >
                  Regenerate
                </RegenerateButton>
              </TopicBlockButtons>
            </TopicBlock>
            <NoticeBox>
              <Text $color="gray" $type="extraSmall">
                Please note, AI Ideas and Summaries regeneration process may
                take a little longer in some cases
              </Text>
            </NoticeBox>
            <ModalContent>
              {!!data && (
                <>
                  <SuggestionRow>
                    <SuggestionTitle>
                      <Text $type="button">
                        Top 10 Plot Twists related to{' '}
                        <TopicName $type="button" as="span" $color="purple">
                          {topic}
                        </TopicName>
                      </Text>
                    </SuggestionTitle>
                    <SuggestionContent>
                      <Text $type="smallMediumBold" as="div">
                        <Ul>
                          {data.suggestion.data[0].plotTwists.map(
                            (item: { plotTwist: string }, index: number) => (
                              <Li key={index}>{item.plotTwist}</Li>
                            ),
                          )}
                        </Ul>
                      </Text>
                    </SuggestionContent>
                  </SuggestionRow>
                  <SuggestionRow>
                    <SuggestionTitle>
                      <Text $type="button">
                        Top 10 Unique Tips & Tricks related to{' '}
                        <TopicName $type="button" as="span" $color="purple">
                          {topic}
                        </TopicName>
                      </Text>
                    </SuggestionTitle>
                    <SuggestionContent>
                      <Text $type="smallMediumBold" as="div">
                        <Ul>
                          {data.suggestion.data[1].tips.map(
                            (item: { tip: string }, index: number) => (
                              <Li key={index}>{item.tip}</Li>
                            ),
                          )}
                        </Ul>
                      </Text>
                    </SuggestionContent>
                  </SuggestionRow>
                  <SuggestionRow>
                    <SuggestionTitle>
                      <Text $type="button">
                        Top 10 Unique Problems and Solutions related to{' '}
                        <TopicName $type="button" as="span" $color="purple">
                          {topic}
                        </TopicName>
                      </Text>
                    </SuggestionTitle>
                    <SuggestionContent>
                      <Text $type="smallMediumBold" as="div">
                        <Ul>
                          {data.suggestion.data[2].problems.map(
                            (
                              item: { problemAndSolution: string },
                              index: number,
                            ) => (
                              <Li key={index}>{item.problemAndSolution}</Li>
                            ),
                          )}
                        </Ul>
                      </Text>
                    </SuggestionContent>
                  </SuggestionRow>
                </>
              )}
            </ModalContent>
          </>
        )}
        <StyledSidePanelFooter>
          <Button $type="secondary" onClick={onClose}>
            Close
          </Button>
        </StyledSidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default AiSuggestionsModal;
